/**
 * Asset registry — the index the workbench and the screenshot harness read.
 *
 * **Adding an asset is one line here.** A sub-agent building `models/otto.ts`
 * registers it, then previews it at `/preview.html?asset=otto` and shoots it with
 * `ASSET=otto npm run shots:asset` without the game needing to exist.
 */

import { calibrationAsset } from './_calibration';
import { ceilingTubeAsset } from './env/ceiling-tube';
import { dogAsset } from './dog';
import { culvertHeadwallAsset } from './env/culvert-headwall';
import { drainChannelAsset } from './env/drain-channel';
import { extractorFanAsset } from './env/extractor-fan';
import { farrowingCrateAsset } from './env/farrowing-crate';
import { feedHopperAsset } from './env/feed-hopper';
import { feedTroughAsset } from './env/feed-trough';
import { finisherAsset, finisherHerdAsset } from './finisher';
import { hallDoorAsset } from './env/hall-door';
import { heatLampAsset } from './env/heat-lamp';
import { highBayLampAsset } from './env/high-bay-lamp';
import { ottoAsset } from './otto';
import { penKerbAsset } from './env/pen-kerb';
import { penWallAsset } from './env/pen-wall';
import { penWallBrokenAsset } from './env/pen-wall-broken';
import { penRubbleAsset } from './env/pen-rubble';
import { wallVentAsset } from './env/wall-vent';
import { wheeledBinAsset } from './env/wheeled-bin';
import { junctionCabinetAsset } from './env/junction-cabinet';
import { feedSacksAsset } from './env/feed-sacks';
import { hoseReelAsset } from './env/hose-reel';
import { hoseNozzleAsset } from './env/hose-nozzle';
import { floorGullyAsset } from './env/floor-gully';
import { personnelDoorAsset } from './env/personnel-door';
import { pressureMatAsset } from './env/pressure-mat';
import { plasticSheetingAsset } from './env/plastic-sheeting';
import { overheadRailAsset } from './env/overhead-rail';
import { hangingShapeAsset } from './env/hanging-shape';
import { bulkheadLampAsset } from './env/bulkhead-lamp';
import { plantMachineAsset } from './env/plant-machine';
import { penGateAsset } from './env/pen-gate';
import { penPartitionAsset } from './env/pen-partition';
import { penRailAsset } from './env/pen-rail';
import { pipeRunAsset } from './env/pipe-run';
import { slatFloorAsset } from './env/slat-floor';
import { slatFloorBrokenAsset } from './env/slat-floor-broken';
import { strawClumpAsset } from './env/straw-clump';
import { stripCurtainAsset } from './env/strip-curtain';
import { workerLegsAsset } from './env/worker-legs';
import { workerAsset } from './env/worker';
import { kestnerAsset } from './env/kestner';
import { kestnerSeatedAsset } from './env/kestnerSeated';
import { pigletAsset } from './piglet';
import { rosalieAsset } from './rosalie';
import { sowAsset } from './sow';
import { truckCabAsset } from './env/truck-cab';
import { boxTrailerAsset } from './env/box-trailer';
import { trailerRampAsset } from './env/trailer-ramp';
import { racePanelAsset } from './env/race-panel';
import { raceGateAsset } from './env/race-gate';
import { palletStackAsset } from './env/pallet-stack';
import { yardFloodlightAsset } from './env/yard-floodlight';
import { perimeterFenceAsset } from './env/perimeter-fence';
import { perimeterGateAsset } from './env/perimeter-gate';
import { fenceBoardsAsset } from './env/fence-boards';
import { puddleAsset } from './env/puddle';
import { grassTuftAsset } from './env/grass-tuft';
import { hedgeSectionAsset } from './env/hedge-section';
import { ditchWaterAsset } from './env/ditch-water';
import { fieldTreeAsset } from './env/field-tree';
import { fuseBoxAsset } from './env/fuse-box';
import { henCrateAsset } from './env/hen-crate';
import { henAsset } from './hen';
import { penCardAsset } from './env/pen-card';
import { torchAsset } from './env/torch';
import { brassBellAsset } from './env/brass-bell';
import { hollyAsset } from './holly';
import { validateAssetMetadata } from './asset-contract';
import type { AssetDefinition } from './types';

/**
 * Registration is the one boundary where metadata is trusted. `getAsset()` may
 * safely use `find()` because this has already rejected duplicate ids.
 */
export function registerAssets(assets: readonly AssetDefinition[]): readonly AssetDefinition[] {
  const ids = new Set<string>();
  for (const asset of assets) {
    validateAssetMetadata(asset);
    if (ids.has(asset.id)) throw new Error(`asset "${asset.id}" registry: duplicate id`);
    ids.add(asset.id);
  }
  return assets;
}

const ASSETS = registerAssets([
  calibrationAsset,
  // ── game assets go here ──────────────────────────────────────────────
  rosalieAsset,
  penKerbAsset,
  penGateAsset,
  penPartitionAsset,
  penRailAsset,
  farrowingCrateAsset,
  slatFloorAsset,
  slatFloorBrokenAsset,
  culvertHeadwallAsset,
  drainChannelAsset,
  feedTroughAsset,
  ceilingTubeAsset,
  extractorFanAsset,
  pipeRunAsset,
  hallDoorAsset,
  // ── Act 1 beat 1, *Born* ─────────────────────────────────────────────
  sowAsset,
  pigletAsset,
  heatLampAsset,
  strawClumpAsset,
  stripCurtainAsset,
  workerLegsAsset,
  workerAsset,
  kestnerAsset,
  // Beat 11's variant: seated, hatless, six live pivots. Its own asset so the
  // pivots are paid for in the office and not in beat 6's yard.
  kestnerSeatedAsset,
  // ── Act 1 beat 3, *Otto* — the fattening hall ────────────────────────
  penWallAsset,
  penWallBrokenAsset,
  penRubbleAsset,
  feedHopperAsset,
  highBayLampAsset,
  wallVentAsset,
  finisherAsset,
  // Listed here rather than under beat 10, which is the beat that places twelve
  // of it in the hedgerow: it is the same animal from the same file, and a
  // reader who finds one and not the other will build a third.
  finisherHerdAsset,
  ottoAsset,
  // ── Act 1 beat 4, *The Service Corridor* ─────────────────────────────
  dogAsset,
  wheeledBinAsset,
  junctionCabinetAsset,
  feedSacksAsset,
  hoseReelAsset,
  hoseNozzleAsset,
  floorGullyAsset,
  personnelDoorAsset,
  pressureMatAsset,
  // ── Act 1 beat 5, *The Cull Corridor* ────────────────────────────────
  plasticSheetingAsset,
  overheadRailAsset,
  hangingShapeAsset,
  bulkheadLampAsset,
  plantMachineAsset,
  // ── Act 1 beat 6, *The Loading Yard* — the first exterior ────────────
  //
  // Authored at the yard's full extent even where beat 6 uses half of it: D-103
  // takes Act 2 and 3's savings by RE-DRESSING this room (beat 12 and beat 13
  // are both `yard` in COLOUR_ARC), so the perimeter, the gate leaves and the
  // race hinge are built once, here. A yard sized only for beat 6 makes shape B
  // cost what shape A costs.
  truckCabAsset,
  boxTrailerAsset,
  trailerRampAsset,
  racePanelAsset,
  raceGateAsset,
  palletStackAsset,
  yardFloodlightAsset,
  perimeterFenceAsset,
  perimeterGateAsset,
  fenceBoardsAsset,
  puddleAsset,
  grassTuftAsset,
  hedgeSectionAsset,
  ditchWaterAsset,
  fieldTreeAsset,
  // ── Act 2 beat 8, *Blackout* — kit only; room dressing follows ────────────
  fuseBoxAsset,
  penCardAsset,
  henCrateAsset,
  henAsset,
  torchAsset,
  // Beat 14's grass prop and carry-shot source. The held variant stays exported
  // from the asset module because the beat owns its parent transform.
  brassBellAsset,
  // ── Act 2 beat 9, *Wool and Wire* ─────────────────────────────────────────
  //
  // She was a commented-out line here for two sessions, which is the honest
  // record of D-075: the companion's SYSTEMS could be built without her, and
  // were, but the character could not be delegated. See `models/holly.ts`.
  hollyAsset,
] as const);

export function listAssets(): readonly AssetDefinition[] {
  return ASSETS;
}

export function getAsset(id: string): AssetDefinition | undefined {
  // `registerAssets()` establishes unique ids above, so first-match lookup is unambiguous.
  return ASSETS.find((a) => a.id === id);
}

/** Ids of real game content, excluding harness scaffolding. */
export function listShippableAssetIds(): string[] {
  return ASSETS.filter((a) => !('harnessOnly' in a)).map((a) => a.id);
}
